import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Plus, Trash2, ArrowUp, ArrowDown, Link2, Save, FileText, Check, Wand2, AlertTriangle,
  GripVertical, ChevronRight, Copy, CopyPlus, Bookmark, Library, X,
} from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import TableFieldEditor from '../../../components/admin/TableFieldEditor';
import { PlatformLinkPicker, insertLinkAtCaret } from '@/components/admin/PlatformLinkPicker';
import DrafterHouseRules from './DrafterHouseRules';

/**
 * Admin · Worksheets — the builder behind the native worksheets.
 *
 * Pick any exercise or supplement, build the fields, publish. The original PDF stays attached and
 * downloadable; the worksheet is what members fill in. Nothing is lost by publishing one.
 */
const FIELD_TYPES = [
  ['heading', 'Section heading'],
  ['instruction', 'Instruction / reading'],
  ['table', 'Table (reference)'],
  ['table_fill', 'Table (they fill in)'],
  ['short_text', 'Short answer'],
  ['paragraph', 'Paragraph'],
  ['list', 'Numbered list'],
  ['multiple_choice', 'Multiple choice'],
  ['checkbox', 'Checkboxes'],
  ['dropdown', 'Dropdown'],
  ['scale', 'Scale'],
  ['date', 'Date'],
];

const newFieldId = () => `f_${Math.random().toString(36).slice(2, 9)}`;

/** Deep copies, each with its own id — options, table columns and flags all come along. */
const cloneFields = (fields) => (fields || []).map((f) => ({
  ...JSON.parse(JSON.stringify(f)), id: newFieldId(),
}));

const blankField = (type) => ({
  id: newFieldId(),
  type,
  label: '',
  help: '',
  required: false,
  journal_prompt: false,
  keep_in_journal: false,
  options: type === 'multiple_choice' || type === 'checkbox' || type === 'dropdown'
    ? ['Option one', 'Option two'] : [],
  allow_other: false,
  scale_min: 1,
  scale_max: 5,
  min_label: '',
  max_label: '',
  rows: type === 'list' ? 1 : 4,
  list_count: 3,
  table_columns: ['table', 'table_fill'].includes(type) ? ['Column one', 'Column two'] : [],
  table_rows: type === 'table' ? [['', '']] : [],
  table_column_types: type === 'table_fill' ? ['text', 'text'] : [],
  table_column_options: type === 'table_fill' ? [[], []] : [],
  table_start_rows: 3,
});

export default function AdminWorksheetsTab() {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(null);       // the worksheet being edited
  const [saving, setSaving] = useState(false);
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState('');
  const [drafting, setDrafting] = useState(null);   // content_id being drafted on its own
  const [closedFields, setClosedFields] = useState({});
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [job, setJob] = useState(null);             // a module being drafted in the background
  const introRef = useRef(null);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/worksheets/fillable-content`, { headers });
      setCatalog(res.data.modules || []);
    } catch (err) { toast.error('Could not load the content list'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  // ── drafting from the attached PDF ─────────────────────────────────────
  const draftOne = async (item) => {
    setDrafting(item.content_id);
    try {
      const res = await axios.post(`${API}/admin/worksheets/draft/${item.content_id}`, {},
        { headers });
      const { fields, prompts, published, flags } = res.data;
      toast.success(
        `${item.title}: ${fields} question${fields === 1 ? '' : 's'}`
        + (prompts ? `, ${prompts} journal prompt${prompts === 1 ? '' : 's'}` : '')
        + (published ? ' — published' : ` — held as a draft (${flags.length} to look at)`),
      );
      await loadCatalog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'That one could not be drafted');
    } finally { setDrafting(null); }
  };

  const draftModule = async (module) => {
    const remaining = module.items.filter((i) => i.has_pdf && !i.worksheet_id).length;
    if (!remaining) { toast.info('Everything in this module already has a worksheet'); return; }
    const publish = window.confirm(
      `Draft ${remaining} worksheet${remaining === 1 ? '' : 's'} from the PDFs in Module `
      + `${module.module_order}? Roughly 15 seconds each.\n\n`
      + 'OK — clean ones publish themselves, anything needing your eye is held as a draft.\n'
      + 'Cancel — hold every one of them back as a draft for you to publish yourself.');
    try {
      const res = await axios.post(
        `${API}/admin/worksheets/draft-module/${module.module_id}?publish=${publish}`,
        {}, { headers });
      setJob({ ...res.data, module_order: module.module_order });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'That module could not be started');
    }
  };

  useEffect(() => {
    if (!job || job.status === 'finished') return undefined;
    const tick = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/worksheets/draft-jobs/${job.id}`, { headers });
        setJob((j) => ({ ...res.data, module_order: j?.module_order }));
        if (res.data.status === 'finished') { loadCatalog(); }
      } catch { /* the next tick tries again */ }
    }, 4000);
    return () => clearInterval(tick);
  }, [job, loadCatalog]);

  const clearFlags = async () => {
    await axios.patch(`${API}/admin/worksheets/${sheet.id}`, { review_flags: [] }, { headers });
    patch({ review_flags: [] });
    toast.success('Flags cleared');
  };

  // What was last written, so leaving with unsaved work asks first. A replace-mode copy or a
  // big block copy is too much to throw away silently (testing agent, iteration 198).
  const savedSnapshot = useRef('');
  const snapshotOf = (s) => JSON.stringify({
    title: s.title, intro: s.intro || '', fields: s.fields,
    visibility: s.visibility, allow_upload: !!s.allow_upload, published: !!s.published,
  });

  const open = async (item) => {
    if (item.worksheet_id) {
      const res = await axios.get(`${API}/admin/worksheets/${item.worksheet_id}`, { headers });
      setSheet(res.data);
      savedSnapshot.current = snapshotOf(res.data);
      // A long worksheet opens closed, so finding the one field he came to change is quick
      setClosedFields((res.data.fields || []).length > 6
        ? Object.fromEntries(res.data.fields.map((f) => [f.id, true])) : {});
    } else {
      const fresh = {
        content_id: item.content_id,
        content_title: item.title,
        title: item.title,
        intro: '',
        fields: [],
        visibility: 'choice',
        published: false,
      };
      setSheet(fresh);
      savedSnapshot.current = snapshotOf(fresh);
      setClosedFields({});
    }
  };

  const leaveBuilder = () => {
    if (snapshotOf(sheet) !== savedSnapshot.current
        && !window.confirm('This worksheet has changes you have not saved. Leave them behind?')) {
      return;
    }
    setSheet(null);
  };

  const patch = (changes) => setSheet((s) => ({ ...s, ...changes }));

  const patchField = (index, changes) => setSheet((s) => ({
    ...s,
    fields: s.fields.map((f, i) => (i === index ? { ...f, ...changes } : f)),
  }));

  const addField = (type) => setSheet((s) => ({ ...s, fields: [...s.fields, blankField(type)] }));

  // ── fields he reuses, kept by name ─────────────────────────────────────
  const [templates, setTemplates] = useState([]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/worksheets/field-templates`, { headers });
      setTemplates(res.data.templates || []);
    } catch { /* the builder is fine without them */ }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const saveAsTemplate = async (index) => {
    const field = sheet.fields[index];
    const name = window.prompt(
      'Save this field to reuse — what should it be called?',
      (field.label || '').split('\n')[0].slice(0, 60));
    if (name === null) return;
    if (!name.trim()) { toast.error('Give it a name first'); return; }
    const clash = templates.find((t) => t.name.toLowerCase() === name.trim().toLowerCase());
    if (clash && !window.confirm(`"${clash.name}" already exists. Replace it?`)) return;
    try {
      await axios.post(`${API}/admin/worksheets/field-templates`,
        { name: name.trim(), field }, { headers });
      toast.success(clash ? `"${name.trim()}" replaced`
        : `Saved as "${name.trim()}" — it is now on every worksheet's Add a field list`);
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save that one');
    }
  };

  const addFromTemplate = (template) => {
    const [copy] = cloneFields([template.field]);
    setSheet((s) => ({ ...s, fields: [...s.fields, copy] }));
    setClosedFields((prev) => ({ ...prev, [copy.id]: false }));
    toast.success(`"${template.name}" added at the end`);
  };

  const removeTemplate = async (template) => {
    if (!window.confirm(`Delete the saved field "${template.name}"? Worksheets already using `
                        + 'it keep theirs.')) return;
    try {
      await axios.delete(`${API}/admin/worksheets/field-templates/${template.id}`, { headers });
      loadTemplates();
      toast.success('Deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete that one');
    }
  };

  // ── starting from a worksheet he has already built ─────────────────────
  const [copyFrom, setCopyFrom] = useState(null);   // { list, search, mode }

  const openCopyFrom = async () => {
    try {
      const res = await axios.get(`${API}/admin/worksheets`, { headers });
      const list = (res.data.worksheets || [])
        .filter((w) => (w.fields || []).length && w.id !== sheet.id);
      if (!list.length) { toast.info('There is no other built worksheet to copy from yet'); return; }
      setCopyFrom({ list, search: '', mode: sheet.fields.length ? 'append' : 'replace' });
    } catch { toast.error('Could not load the other worksheets'); }
  };

  const copyFieldsFrom = (source) => {
    const copies = cloneFields(source.fields);
    const replacing = copyFrom.mode === 'replace';
    setSheet((s) => ({ ...s, fields: replacing ? copies : [...s.fields, ...copies] }));
    const closedCopies = Object.fromEntries(copies.map((f) => [f.id, true]));
    setClosedFields((prev) => (replacing ? closedCopies : { ...prev, ...closedCopies }));
    setCopyFrom(null);
    toast.success(`${copies.length} field${copies.length === 1 ? '' : 's'} from `
      + `"${source.title}" ${replacing ? 'copied in' : 'added at the end'} — `
      + 'nothing is saved until you press Save');
  };


  /** A copy of the field, with its own id, dropped in directly below the original. */
  const duplicateField = (index) => {
    let copyId = null;
    setSheet((s) => {
      const [copy] = cloneFields([s.fields[index]]);
      copyId = copy.id;
      const next = [...s.fields];
      next.splice(index + 1, 0, copy);
      return { ...s, fields: next };
    });
    setClosedFields((prev) => (copyId ? { ...prev, [copyId]: false } : prev));
    toast.success('Field duplicated — the copy is just below');
  };

  /** A heading and everything under it, up to the next heading, copied as one chunk. */
  const duplicateBlock = (index) => {
    let made = [];
    setSheet((s) => {
      const rest = s.fields.slice(index + 1);
      const nextHeading = rest.findIndex((f) => f.type === 'heading');
      const end = nextHeading === -1 ? s.fields.length : index + 1 + nextHeading;
      made = cloneFields(s.fields.slice(index, end));
      const next = [...s.fields];
      next.splice(end, 0, ...made);
      return { ...s, fields: next };
    });
    setClosedFields((prev) => ({
      ...prev, ...Object.fromEntries(made.map((f) => [f.id, true])),
    }));
    const under = made.length - 1;
    toast.success(`Section copied — the heading and ${under} field${under === 1 ? '' : 's'} `
                  + 'under it, placed below the original');
  };

  const moveField = (index, dir) => setSheet((s) => {
    const next = [...s.fields];
    const target = index + dir;
    if (target < 0 || target >= next.length) return s;
    [next[index], next[target]] = [next[target], next[index]];
    return { ...s, fields: next };
  });

  /** Dropped on the field at `to`: the dragged one takes that place, the rest close up. */
  const dropField = (from, to) => {
    if (from === null || to === null || from === to) return;
    setSheet((s) => {
      const next = [...s.fields];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...s, fields: next };
    });
  };

  // Dragging a field by its handle. Pointer events rather than HTML5 drag-and-drop, because
  // the handle has to arm the drag on mousedown — a `draggable` attribute set in the same
  // gesture comes too late for the browser, and it hijacks text selection in the inputs.
  const rowRefs = useRef([]);
  const pointerY = useRef(null);
  useEffect(() => {
    if (dragFrom === null) return undefined;

    const rowUnder = (y) => rowRefs.current.findIndex((el) => {
      if (!el) return false;
      const box = el.getBoundingClientRect();
      return y >= box.top && y <= box.bottom;
    });

    const move = (e) => {
      pointerY.current = e.clientY;
      const index = rowUnder(e.clientY);
      if (index !== -1) setDragOver(index);
    };
    const up = (e) => {
      const index = rowUnder(e.clientY);
      dropField(dragFrom, index === -1 ? null : index);
      setDragFrom(null);
      setDragOver(null);
    };

    // Near the top or bottom edge the page creeps along, so a field can travel the whole
    // length of a fifty-field worksheet without letting go.
    const creep = setInterval(() => {
      const y = pointerY.current;
      if (y === null) return;
      if (y < 140) window.scrollBy(0, -18);
      else if (y > window.innerHeight - 140) window.scrollBy(0, 18);
    }, 40);

    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      clearInterval(creep);
      pointerY.current = null;
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [dragFrom]);

  const removeField = (index) => setSheet((s) => ({
    ...s, fields: s.fields.filter((_, i) => i !== index),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        content_id: sheet.content_id,
        title: sheet.title,
        intro: sheet.intro,
        fields: sheet.fields,
        visibility: sheet.visibility,
        allow_upload: !!sheet.allow_upload,
        published: sheet.published,
      };
      const res = await axios.post(`${API}/admin/worksheets`, body, { headers });
      setSheet((s) => ({ ...s, id: res.data.id }));
      savedSnapshot.current = snapshotOf(sheet);
      toast.success(sheet.published ? 'Saved and live' : 'Saved as a draft');
      loadCatalog();
    } catch (err) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!sheet.id) { setSheet(null); return; }
    if (!window.confirm('Delete this worksheet? Members\' answers to it go too.')) return;
    await axios.delete(`${API}/admin/worksheets/${sheet.id}`, { headers });
    setSheet(null);
    toast.success('Worksheet deleted');
    loadCatalog();
  };

  // "Insert platform link" drops into whichever box was last clicked. Only the boxes HE writes
  // for members — instructions, section headings, paragraphs and instruction/reading blocks —
  // are linkable; question labels for the participant to answer are left as plain words.
  const LINKABLE_TYPES = ['heading', 'paragraph', 'instruction', 'table'];
  const linkTarget = useRef({ kind: 'intro', el: null });
  const [linkTargetName, setLinkTargetName] = useState('Instructions');

  const aimAt = (target, name) => () => {
    linkTarget.current = target;
    setLinkTargetName(name);
  };

  const linkableFocus = (field, index, prop) => (
    LINKABLE_TYPES.includes(field.type)
      ? (e) => {
        linkTarget.current = { kind: 'field', index, prop, el: e.target };
        const label = (FIELD_TYPES.find(([t]) => t === field.type) || [])[1] || field.type;
        setLinkTargetName(prop === 'help' ? `${label} — help text` : label);
      }
      : undefined
  );

  const insertLink = (item) => {
    const target = linkTarget.current;
    const el = target.el || introRef.current;
    const current = target.kind === 'field'
      ? (sheet.fields[target.index]?.[target.prop] || '')
      : (sheet.intro || '');
    const { value, caret } = insertLinkAtCaret(el, current, item);
    if (target.kind === 'field') patchField(target.index, { [target.prop]: value });
    else patch({ intro: value });
    requestAnimationFrame(() => {
      if (el) { el.focus(); el.setSelectionRange(caret, caret); }
    });
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;
  }

  // ── the builder ────────────────────────────────────────────────────────
  if (sheet) {
    return (
      <div className="space-y-4" data-testid="worksheet-builder">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" className="border-white/20 text-gray-100 hover:bg-white/10" size="sm" onClick={leaveBuilder}
            data-testid="worksheet-builder-back">← All content</Button>
          <span className="text-xs text-gray-400">{sheet.content_title}</span>
          <div className="ml-auto flex items-center gap-2">
            <Switch checked={!!sheet.published} onCheckedChange={(v) => patch({ published: v })}
              data-testid="worksheet-published-toggle" />
            <span className="text-xs text-gray-400">
              {sheet.published ? 'Live for members' : 'Draft'}
            </span>
            <Button size="sm" onClick={save} disabled={saving} data-testid="worksheet-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
            </Button>
            <Button size="sm" variant="ghost" className="text-red-300" onClick={remove}
              data-testid="worksheet-delete-btn"><Trash2 className="w-4 h-4" /></Button>
          </div>
        </div>

        {(sheet.review_flags || []).length > 0 && (
          <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 p-4 space-y-2"
            data-testid="worksheet-review-flags">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm text-amber-100 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                This one needs your eye — it was written for the old format, so it is being held
                as a draft.
              </p>
              <Button size="sm" variant="outline"
                className="border-amber-400/40 text-amber-100 hover:bg-amber-400/20 shrink-0"
                onClick={clearFlags} data-testid="worksheet-clear-flags">
                I&apos;ve dealt with these
              </Button>
            </div>
            <ul className="space-y-1.5">
              {sheet.review_flags.map((flag, i) => (
                <li key={i} className="text-xs text-amber-100/80">
                  <span className="text-amber-200">{flag.reason}</span>
                  {flag.quote && <span className="block text-amber-100/50 italic mt-0.5">
                    &ldquo;{flag.quote}&rdquo;
                  </span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-3">
          <div>
            <Label className="text-xs text-gray-400">Worksheet title</Label>
            <Input value={sheet.title} onChange={(e) => patch({ title: e.target.value })}
              className="bg-black/30 border-white/10 text-gray-100 placeholder:text-gray-500" data-testid="worksheet-title-input" />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-gray-400">
                Instructions (bold, lists and platform links all work)
              </Label>
              <div className="text-right">
                <Button size="sm" variant="outline" className="border-white/20 text-gray-100 hover:bg-white/10" onClick={() => setPicking(true)}
                  data-testid="worksheet-insert-link-btn">
                  <Link2 className="w-3.5 h-3.5 mr-1" /> Insert platform link
                </Button>
                <p className="text-[10px] text-gray-500 mt-1" data-testid="worksheet-link-target">
                  Goes into: {linkTargetName}
                </p>
              </div>
            </div>
            <textarea ref={introRef} rows={6} value={sheet.intro || ''}
              onFocus={(e) => aimAt({ kind: 'intro', el: e.target }, 'Instructions')()}
              onChange={(e) => patch({ intro: e.target.value })}
              className="w-full rounded-md bg-black/30 border border-white/10 p-2 text-sm text-gray-100 placeholder:text-gray-500 mt-1"
              data-testid="worksheet-intro-input" />
          </div>

          <div>
            <Label className="text-xs text-gray-400">Handing in</Label>
            <select value={sheet.visibility} onChange={(e) => patch({ visibility: e.target.value })}
              className="w-full rounded-md bg-black/30 border border-white/10 p-2 text-sm text-gray-100"
              data-testid="worksheet-visibility-select">
              <option value="none">No hand-in — nothing to submit, no button shown</option>
              <option value="private">Just for them — they write, only they ever read it</option>
              <option value="homework">Hand in to the section practitioner</option>
              <option value="choice">Let them decide when they finish</option>
            </select>
            {sheet.visibility === 'none' && (
              <p className="text-[11px] text-cyan-300/80 mt-1" data-testid="worksheet-no-handin-note">
                For an exercise they DO rather than write, or a supplement that is simply read.
                Anything they type still saves itself as they go.
              </p>
            )}
          </div>

          <label className="flex items-start gap-2 text-sm text-gray-300 cursor-pointer">
            <input type="checkbox" className="mt-1" checked={!!sheet.allow_upload}
              onChange={(e) => patch({ allow_upload: e.target.checked })}
              data-testid="worksheet-allow-upload-toggle" />
            <span>
              Let them hand in a file with this one
              <span className="block text-xs text-gray-500">
                Adds an attach button beside Submit, for anyone who printed the blank copy and
                filled it in by hand.
              </span>
            </span>
          </label>
        </div>

        {/* Fields */}
        {sheet.fields.length > 3 && (
          <div className="flex items-center justify-between gap-3 text-xs">
            <p className="text-gray-400">
              {sheet.fields.length} fields · drag the handle to move one
            </p>
            <div className="flex gap-2">
              <button onClick={() => setClosedFields({})}
                      className="text-gray-400 hover:text-white transition-colors"
                      data-testid="worksheet-expand-all">Expand all</button>
              <button onClick={() => setClosedFields(
                        Object.fromEntries(sheet.fields.map((f) => [f.id, true])))}
                      className="text-gray-400 hover:text-white transition-colors"
                      data-testid="worksheet-collapse-all">Collapse all</button>
            </div>
          </div>
        )}
        <div className="space-y-3">
          {sheet.fields.map((field, i) => (
            <div key={field.id} ref={(el) => { rowRefs.current[i] = el; }}
              className={`rounded-xl bg-white/5 border p-4 space-y-2 transition-colors
                ${dragOver === i && dragFrom !== null && dragFrom !== i
                  ? 'border-amber-300/70' : 'border-white/10'}
                ${dragFrom === i ? 'opacity-40' : ''}`}
              data-testid={`worksheet-field-row-${i}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <button onMouseDown={(e) => { e.preventDefault(); setDragFrom(i); setDragOver(i); }}
                  className="cursor-grab text-gray-500 hover:text-gray-200 active:cursor-grabbing
                             transition-colors"
                  title="Drag to move this field"
                  data-testid={`worksheet-field-grip-${i}`}>
                  <GripVertical className="w-4 h-4" />
                </button>
                <span className="text-[10px] uppercase tracking-wider text-amber-300">
                  {(FIELD_TYPES.find(([t]) => t === field.type) || [])[1] || field.type}
                </span>
                <button onClick={() => setClosedFields((prev) =>
                          ({ ...prev, [field.id]: !prev[field.id] }))}
                  className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-gray-400
                             hover:text-white transition-colors"
                  data-testid={`worksheet-field-toggle-${i}`}>
                  <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform
                    ${closedFields[field.id] ? '' : 'rotate-90'}`} />
                  <span className="truncate text-xs">
                    {(field.label || '').split('\n')[0].slice(0, 90) || 'Untitled'}
                  </span>
                </button>
                <div className="ml-auto flex gap-1">
                  <Button size="sm" variant="outline" className="border-white/20 text-gray-100 hover:bg-white/10" onClick={() => moveField(i, -1)} disabled={i === 0}
                    data-testid={`worksheet-field-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="border-white/20 text-gray-100 hover:bg-white/10" onClick={() => moveField(i, 1)}
                    disabled={i === sheet.fields.length - 1}
                    data-testid={`worksheet-field-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="border-white/20 text-gray-100 hover:bg-white/10"
                    onClick={() => duplicateField(i)} title="Duplicate this field"
                    aria-label="Duplicate this field"
                    data-testid={`worksheet-field-duplicate-${i}`}><Copy className="w-3.5 h-3.5" /></Button>
                  {field.type === 'heading' && (
                    <Button size="sm" variant="outline" className="border-white/20 text-gray-100 hover:bg-white/10"
                      onClick={() => duplicateBlock(i)}
                      title="Duplicate this heading and every field under it"
                      aria-label="Duplicate this heading and every field under it"
                      data-testid={`worksheet-field-duplicate-block-${i}`}>
                      <CopyPlus className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="border-white/20 text-gray-100 hover:bg-white/10"
                    onClick={() => saveAsTemplate(i)} title="Save this field to reuse by name"
                    aria-label="Save this field to reuse by name"
                    data-testid={`worksheet-field-save-template-${i}`}>
                    <Bookmark className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-300"
                    onClick={() => removeField(i)}
                    data-testid={`worksheet-field-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>

              {!closedFields[field.id] && (
              <div className="space-y-2">
              {field.type === 'instruction' ? (
                <>
                  <textarea rows={5} value={field.label}
                    onFocus={linkableFocus(field, i, 'label')}
                    onChange={(e) => patchField(i, { label: e.target.value })}
                    placeholder="What they need to read or do here — nothing to fill in"
                    className="w-full rounded-md bg-black/30 border border-white/10 p-2 text-sm
                               text-gray-100 placeholder:text-gray-500 leading-relaxed"
                    data-testid={`worksheet-field-label-${i}`} />
                  <p className="text-[11px] text-gray-500">
                    Shown as body text on the worksheet. Blank lines make paragraphs. Platform links
                    work here — click into this box, then press Insert platform link.
                  </p>
                </>
              ) : (
                <>
                  <Input value={field.label}
                    onFocus={linkableFocus(field, i, 'label')}
                    onChange={(e) => patchField(i, { label: e.target.value })}
                    placeholder={field.type === 'heading' ? 'Heading'
                      : field.type === 'table' ? 'Table caption (optional)'
                      : field.type === 'table_fill' ? 'What this grid is for' : 'The question'}
                    className="bg-black/30 border-white/10 text-gray-100 placeholder:text-gray-500" data-testid={`worksheet-field-label-${i}`} />

                  <Input value={field.help}
                    onFocus={linkableFocus(field, i, 'help')}
                    onChange={(e) => patchField(i, { help: e.target.value })}
                    placeholder="Help text under the question (optional)"
                    className="bg-black/30 border-white/10 text-xs text-gray-100 placeholder:text-gray-500"
                    data-testid={`worksheet-field-help-${i}`} />
                </>
              )}

              {['multiple_choice', 'checkbox', 'dropdown'].includes(field.type) && (
                <div className="space-y-2">
                  <Label className="text-xs text-gray-400">Options, one per line</Label>
                  <textarea rows={3} value={(field.options || []).join('\n')}
                    onChange={(e) => patchField(i, { options: e.target.value.split('\n') })}
                    className="w-full rounded-md bg-black/30 border border-white/10 p-2 text-sm text-gray-100 placeholder:text-gray-500"
                    data-testid={`worksheet-field-options-${i}`} />
                  <label className="flex items-center gap-2 text-xs text-gray-400">
                    <input type="checkbox" checked={!!field.allow_other}
                      onChange={(e) => patchField(i, { allow_other: e.target.checked })}
                      data-testid={`worksheet-field-other-${i}`} />
                    Add an "Other ___" option
                  </label>
                </div>
              )}

              {field.type === 'scale' && (
                <div className="flex gap-2 flex-wrap">
                  <Input type="number" value={field.scale_min}
                    onChange={(e) => patchField(i, { scale_min: parseInt(e.target.value, 10) || 1 })}
                    className="bg-black/30 border-white/10 text-gray-100 placeholder:text-gray-500 w-20" placeholder="Min" />
                  <Input type="number" value={field.scale_max}
                    onChange={(e) => patchField(i, { scale_max: parseInt(e.target.value, 10) || 5 })}
                    className="bg-black/30 border-white/10 text-gray-100 placeholder:text-gray-500 w-20" placeholder="Max" />
                  <Input value={field.min_label} onChange={(e) => patchField(i, { min_label: e.target.value })}
                    className="bg-black/30 border-white/10 text-gray-100 placeholder:text-gray-500 flex-1" placeholder="Label at the low end" />
                  <Input value={field.max_label} onChange={(e) => patchField(i, { max_label: e.target.value })}
                    className="bg-black/30 border-white/10 text-gray-100 placeholder:text-gray-500 flex-1" placeholder="Label at the high end" />
                </div>
              )}

              {['table', 'table_fill'].includes(field.type) && (
                <TableFieldEditor field={field} index={i}
                  onChange={(patch) => patchField(i, patch)} />
              )}

              {field.type === 'list' && (
                <div className="flex flex-wrap items-center gap-2">
                  <Input type="number" min={1} max={20} value={field.list_count ?? 3}
                    onChange={(e) => patchField(i, { list_count: parseInt(e.target.value, 10) || 3 })}
                    className="bg-black/30 border-white/10 text-gray-100 w-20"
                    data-testid={`worksheet-field-list-count-${i}`} />
                  <span className="text-[11px] text-gray-500">
                    numbered blanks under the prompt — ask once, they answer 1, 2, 3…
                  </span>
                  <Input type="number" min={1} max={6} value={field.rows || 1}
                    onChange={(e) => patchField(i, { rows: parseInt(e.target.value, 10) || 1 })}
                    className="bg-black/30 border-white/10 text-gray-100 w-20"
                    data-testid={`worksheet-field-list-rows-${i}`} />
                  <span className="text-[11px] text-gray-500">
                    lines tall each blank is (1 for words or a phrase)
                  </span>
                </div>
              )}

              {field.type === 'paragraph' && (
                <div className="flex items-center gap-2">
                  <Input type="number" min={1} value={field.rows}
                    onChange={(e) => patchField(i, { rows: parseInt(e.target.value, 10) || 4 })}
                    className="bg-black/30 border-white/10 text-gray-100 placeholder:text-gray-500 w-20"
                    data-testid={`worksheet-field-rows-${i}`} />
                  <span className="text-[11px] text-gray-500">
                    Lines tall the answer box is (and blank lines on the printed copy). They can
                    always write more than fits — it scrolls.
                  </span>
                </div>
              )}

              {!['heading', 'instruction', 'table'].includes(field.type) && (
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-xs text-gray-400">
                    <input type="checkbox" checked={!!field.required}
                      onChange={(e) => patchField(i, { required: e.target.checked })}
                      data-testid={`worksheet-field-required-${i}`} />
                    Required
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-400">
                    <input type="checkbox" checked={!!field.journal_prompt}
                      onChange={(e) => patchField(i, {
                        journal_prompt: e.target.checked,
                        // keeping it forever only means anything if it is a journal prompt
                        keep_in_journal: e.target.checked ? field.keep_in_journal : false,
                      })}
                      data-testid={`worksheet-field-journal-prompt-${i}`} />
                    This is a journal prompt
                    <span className="text-gray-500">
                      — adds &ldquo;Add to my journal&rdquo; beside it
                    </span>
                  </label>
                  {field.journal_prompt && (
                    <label className="flex items-center gap-2 text-xs text-gray-400 pl-5">
                      <input type="checkbox" checked={!!field.keep_in_journal}
                        onChange={(e) => patchField(i, { keep_in_journal: e.target.checked })}
                        data-testid={`worksheet-field-keep-in-journal-${i}`} />
                      Keep it in their journal
                      <span className="text-gray-500">
                        — once they have done this worksheet, it stays as a prompt they can tap
                        any time
                      </span>
                    </label>
                  )}
                </div>
              )}
              </div>
              )}
            </div>
          ))}
        </div>

        <div className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-4">
          <div>
            <p className="text-xs text-gray-400 mb-2">Add a field</p>
            <div className="flex flex-wrap gap-2">
              {FIELD_TYPES.map(([type, label]) => (
                <Button key={type} size="sm" variant="outline" className="border-white/20 text-gray-100 hover:bg-white/10" onClick={() => addField(type)}
                  data-testid={`worksheet-add-${type}`}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> {label}
                </Button>
              ))}
            </div>
          </div>

          <div className="border-t border-white/10 pt-3" data-testid="worksheet-templates">
            <p className="text-xs text-gray-400 mb-2">Your saved fields</p>
            {templates.length === 0 ? (
              <p className="text-[11px] text-gray-400">
                None yet — press the bookmark on any field above to keep it here by name, ready
                to drop into any worksheet.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {templates.map((t) => (
                  <span key={t.id}
                    className="inline-flex items-center rounded-md border border-white/20
                               overflow-hidden"
                    data-testid={`worksheet-template-${t.id}`}>
                    <button onClick={() => addFromTemplate(t)}
                      className="px-2.5 py-1.5 text-xs text-gray-100 hover:bg-white/10
                                 transition-colors"
                      data-testid={`worksheet-template-add-${t.id}`}>
                      <Bookmark className="w-3 h-3 mr-1 inline" /> {t.name}
                    </button>
                    <button onClick={() => removeTemplate(t)} title="Delete this saved field"
                      aria-label={`Delete the saved field ${t.name}`}
                      className="px-1.5 py-1.5 text-gray-300 hover:text-red-300 hover:bg-white/10
                                 transition-colors"
                      data-testid={`worksheet-template-remove-${t.id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-white/10 pt-3 flex flex-wrap items-center gap-3">
            <Button size="sm" variant="outline"
              className="border-white/20 text-gray-100 hover:bg-white/10"
              onClick={openCopyFrom} data-testid="worksheet-copy-from-btn">
              <Library className="w-3.5 h-3.5 mr-1" /> Copy from another worksheet
            </Button>
            <p className="text-[11px] text-gray-400">
              Brings a finished worksheet&apos;s fields in as a starting point. Nothing is saved
              until you press Save.
            </p>
          </div>
        </div>

        {copyFrom && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start
                          justify-center p-6 overflow-y-auto"
            data-testid="worksheet-copy-from-modal">
            <div className="w-full max-w-xl rounded-xl bg-[#12161c] border border-white/10 p-4
                            space-y-3 mt-10">
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-100">Copy fields from</p>
                <Button size="sm" variant="ghost" className="ml-auto text-gray-400"
                  onClick={() => setCopyFrom(null)} data-testid="worksheet-copy-from-close">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                {[['append', 'Add to the end of this one'], ['replace', 'Replace what is here']]
                  .map(([mode, label]) => (
                    <button key={mode} onClick={() => setCopyFrom((c) => ({ ...c, mode }))}
                      className={`px-2.5 py-1.5 rounded-md border transition-colors ${
                        copyFrom.mode === mode
                          ? 'border-amber-300/70 bg-amber-300/15 text-amber-100'
                          : 'border-white/20 text-gray-300 hover:bg-white/10'}`}
                      data-testid={`worksheet-copy-from-mode-${mode}`}>
                      {label}
                    </button>
                  ))}
              </div>
              <Input value={copyFrom.search}
                onChange={(e) => setCopyFrom((c) => ({ ...c, search: e.target.value }))}
                placeholder="Search your built worksheets…"
                className="bg-black/30 border-white/10 text-gray-100 placeholder:text-gray-500"
                data-testid="worksheet-copy-from-search" />
              <div className="max-h-[50vh] overflow-y-auto space-y-1.5">
                {copyFrom.list
                  .filter((w) => (w.title || '').toLowerCase()
                    .includes(copyFrom.search.trim().toLowerCase()))
                  .map((w) => (
                    <button key={w.id} onClick={() => copyFieldsFrom(w)}
                      className="w-full text-left rounded-lg border border-white/10 bg-white/5 p-3
                                 hover:bg-white/10 transition-colors"
                      data-testid={`worksheet-copy-from-pick-${w.id}`}>
                      <p className="text-sm text-gray-100 truncate">{w.title}</p>
                      <p className="text-[11px] text-gray-500">
                        {w.fields.length} field{w.fields.length === 1 ? '' : 's'}
                        {w.published ? ' · live' : ' · draft'}
                      </p>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}

        {picking && (
          <PlatformLinkPicker onInsert={insertLink} onClose={() => setPicking(false)} />
        )}
      </div>
    );
  }

  // ── the content list ───────────────────────────────────────────────────
  const filtered = catalog
    .map((m) => ({
      ...m,
      items: m.items.filter((i) =>
        !search.trim()
        || i.title.toLowerCase().includes(search.toLowerCase())
        || (i.section || '').toLowerCase().includes(search.toLowerCase())
        || (m.module_title || '').toLowerCase().includes(search.toLowerCase())),
    }))
    .filter((m) => m.items.length);

  return (
    <div className="space-y-4" data-testid="admin-worksheets-tab">
      <DrafterHouseRules />
      <div className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-3">
        <p className="text-sm text-gray-300">
          Pick any exercise or supplement and build the interactive version — or press{' '}
          <span className="text-amber-300">Draft from the PDF</span> and let it read the document
          and lay the worksheet out for you. A document with printed blanks is transcribed word
          for word; one that is only instructions has the form designed from them. The PDF stays
          attached and downloadable either way.
        </p>
        <p className="text-xs text-gray-500">
          Every draft is held for your eye unless you switch on self-publishing above. Anything
          written for the old format — &ldquo;post this under the header&rdquo;, a week that was
          never uploaded — is flagged for you.
        </p>
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by exercise, supplement, section or module…"
          className="bg-black/30 border-white/10 text-gray-100 placeholder:text-gray-500" data-testid="worksheet-search" />
      </div>

      {job && (
        <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 p-4 space-y-2"
          data-testid="worksheet-draft-job">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-amber-100">
              {job.status === 'finished'
                ? `Module ${job.module_order} done — ${job.done} of ${job.total} drafted.`
                : `Drafting Module ${job.module_order} — ${job.done} of ${job.total}…`}
            </p>
            {job.status === 'finished' ? (
              <Button size="sm" variant="outline"
                className="border-amber-400/40 text-amber-100 hover:bg-amber-400/20"
                onClick={() => setJob(null)} data-testid="worksheet-draft-job-close">Close</Button>
            ) : <Loader2 className="w-4 h-4 animate-spin text-amber-300" />}
          </div>
          <div className="h-1 rounded-full bg-black/30 overflow-hidden">
            <div className="h-full bg-amber-300/60 transition-all duration-700"
              style={{ width: `${Math.round((job.done / Math.max(job.total, 1)) * 100)}%` }} />
          </div>
          <ul className="space-y-1">
            {(job.results || []).map((r) => (
              <li key={r.content_id} className="text-xs text-amber-100/80">
                {r.ok
                  ? `${r.published ? '✓ published' : '• draft'} — ${r.title} · ${r.fields} questions`
                    + (r.prompts ? `, ${r.prompts} prompts` : '')
                    + (r.flags?.length ? ` · ${r.flags.length} to look at` : '')
                  : `✕ ${r.title || r.content_id} — ${r.error}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {filtered.map((module) => (
        <div key={module.module_id} className="rounded-xl bg-white/5 border border-white/10 p-4"
          data-testid={`worksheet-module-${module.module_order}`}>
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <p className="text-sm text-gray-100 font-medium">
              Module {module.module_order} — {module.module_title}
            </p>
            <Button size="sm" variant="outline"
              className="border-amber-400/40 text-amber-200 hover:bg-amber-400/10"
              disabled={!!job && job.status !== 'finished'}
              onClick={() => draftModule(module)}
              data-testid={`worksheet-draft-module-${module.module_order}`}>
              <Wand2 className="w-3.5 h-3.5 mr-1" /> Draft this module
            </Button>
          </div>
          <div className="divide-y divide-white/5">
            {module.items.map((item) => (
              <div key={item.content_id}
                className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-white/5">
                <button type="button" onClick={() => open(item)}
                  data-testid={`worksheet-content-${item.content_id}`}
                  className="flex items-center gap-3 text-left flex-1 min-w-0">
                  <FileText className="w-4 h-4 text-gray-500 shrink-0" />
                  <span className="text-sm text-gray-200 truncate">{item.title}</span>
                  {item.section && (
                    <span className="text-xs text-gray-500 shrink-0 hidden sm:inline">
                      {item.section}
                    </span>
                  )}
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 shrink-0">
                    {item.content_type}
                  </span>
                </button>

                {item.kept > 0 && (
                  <span className="text-[11px] text-cyan-300/80 shrink-0"
                    title="Prompts that stay in their journal once they have done this worksheet"
                    data-testid={`worksheet-kept-${item.content_id}`}>
                    {item.kept} kept
                  </span>
                )}

                {item.flags > 0 && (
                  <span className="text-xs text-amber-300 flex items-center gap-1 shrink-0"
                    data-testid={`worksheet-flagged-${item.content_id}`}>
                    <AlertTriangle className="w-3.5 h-3.5" /> {item.flags}
                  </span>
                )}
                {item.worksheet_id
                  ? <span className={`text-xs shrink-0 flex items-center gap-1 ${
                      item.published ? 'text-emerald-400' : 'text-gray-400'}`}>
                      <Check className="w-3.5 h-3.5" /> {item.published ? 'live' : 'draft'}
                    </span>
                  : item.has_pdf
                    ? <Button size="sm" variant="outline"
                        className="border-amber-400/30 text-amber-200 hover:bg-amber-400/10 shrink-0"
                        disabled={drafting === item.content_id}
                        onClick={() => draftOne(item)}
                        data-testid={`worksheet-draft-${item.content_id}`}>
                        {drafting === item.content_id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <><Wand2 className="w-3.5 h-3.5 mr-1" /> Draft from the PDF</>}
                      </Button>
                    : <span className="text-xs text-gray-500 shrink-0">no PDF · build by hand</span>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
